import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, X } from 'lucide-react';
import { useAdminView } from '@/context/AdminViewContext';
import { useLanguage } from '@/context/LanguageContext';

/**
 * Persistent pill visible only when an admin has switched to "view as
 * customer" mode. Lets them jump back to the admin dashboard from any
 * customer page they might land on. Hidden on the /admin/* routes themselves.
 */
const FloatingAdminReturn = () => {
  const { isAdminAsCustomer, setViewAsCustomer } = useAdminView();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  if (!isAdminAsCustomer) return null;
  if (location.pathname.startsWith('/admin')) return null;

  // Bookmark flyer occupies the bottom-4/right-4 corner (or left in RTL); sit
  // well above it so both fit comfortably on top of MobileBottomNav. On mobile
  // we anchor even higher because the flyer is taller when wrapped.
  const side = lang === 'ar' ? 'left-4' : 'right-4';

  return (
    <div
      className={`fixed bottom-64 md:bottom-56 ${side} z-[60] flex items-center gap-2 pl-3 pr-1 py-1 rounded-full shadow-lg border`}
      style={{
        background: 'linear-gradient(135deg, #0f172a, #1e293b)',
        borderColor: 'rgba(255,255,255,0.15)',
        color: '#f8fafc',
      }}
      data-testid="admin-return-pill"
    >
      <Shield className="w-4 h-4 text-orange-300 shrink-0" />
      <button
        onClick={() => { setViewAsCustomer(false); navigate('/admin'); }}
        className="text-xs font-medium hover:underline"
        data-testid="admin-return-button"
      >
        {t('admin.returnToAdmin', 'Return to admin')}
      </button>
      <button
        onClick={() => setViewAsCustomer(false)}
        aria-label={t('common.close', 'Close')}
        className="ml-1 w-7 h-7 rounded-full grid place-items-center hover:bg-white/10"
        data-testid="admin-return-close-button"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default FloatingAdminReturn;
